import React from 'react';
import { communityUrls, companyUrls } from 'urls';
import LinkAsButton from 'ui/elements/buttons/LinkAsButton';
import { DashboardMessage as DashboardMessageDTO } from 'apis/DashboardAPI';
import CommunityRequests from 'pages/Community/Overview/CommunityRequests';
import Card from 'ui/views/cards/Card';
import { yearsMonthsDaysSince } from 'util/dateUtils';
import PlusIcon from 'ui/elements/icons/PlusIcon';
import StatsCard from 'ui/views/cards/StatsCard';
import { differenceInDays, startOfDay } from 'date-fns';
import CardHeading from 'ui/views/cards/CardHeading';
import FinishCompanyProfile from '../sections/messages/FinishCompanyProfile';
import PendingCompanyMessage from '../sections/messages/PendingCompanyMessage';
import useRoute from 'hooks/useRoute';
import { AgendaCardContainer, UpdateButton } from './OnYourCompanyAgenda';
import { useCloseableMessage } from 'apis/CompanyAPI/users/useUiState';
import { IconButton } from '@mui/material';
import CloseIcon from 'ui/elements/icons/CloseIcon';
import styled from '@emotion/styled';
import { quarterSpacing } from 'ui/theme/themeConstants';

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: ${quarterSpacing};
  right: ${quarterSpacing};
  z-index: 1;
`;

function isCompanyMessage(type: DashboardMessageDTO['type']) {
  return [
    'community-admin-pending-requests',
    'welcome-to-company-profile-as-new-company-admin',
    'should-update-company-profile-as-company-admin',
    'should-update-investors-as-company-admin',
    'pending-community-application-as-company-admin',
    'dnb-nxt-webinar',
    'is-founder-in-100-pitches',
    'company-pending-in-community',
  ].includes(type);
}

export function CompanyMessages({
  messages,
  excludeMessages,
}: {
  messages: DashboardMessageDTO[];
  excludeMessages?: DashboardMessageDTO['type'][];
}) {
  const { push } = useRoute();

  // Closeable message hooks for company messages
  const updateInvestors = useCloseableMessage('should-update-investors-as-company-admin');
  const updateCompanyProfile = useCloseableMessage('should-update-company-profile-as-company-admin');
  const pendingApplication = useCloseableMessage('pending-community-application-as-company-admin');

  const restOfMessages = messages
    .filter(it => isCompanyMessage(it.type))
    .filter(it => !(excludeMessages || []).includes(it.type));

  return restOfMessages.map(message => {
    switch (message.type) {
      case 'company-pending-in-community':
        return <PendingCompanyMessage message={message} key={message.type + message.community.name} />;
      case 'community-admin-pending-requests':
        return (
          <CommunityRequests
            key={`${message.communitySlug}-message-community-requests`}
            communitySlug={message.communitySlug}
            users={message.userCount}
            companies={message.companyCount}
          />
        );
      case 'should-update-investors-as-company-admin':
        if (updateInvestors.isClosed) return null;

        const updateUrl = companyUrls.overview(message.company.slug, 'updates', {
          isPostingUpdate: true,
          audience: 'investors',
        });
        if (!message.lastUpdated) {
          return (
            <div key="message-update-investors" style={{ position: 'relative' }}>
              <CloseButtonWrapper>
                <IconButton size="small" onClick={updateInvestors.closeMessage} aria-label="Close">
                  <CloseIcon />
                </IconButton>
              </CloseButtonWrapper>
              <Card>
                <AgendaCardContainer>
                  <CardHeading heading="Your investors are waiting" />
                  <p className="text-body u-content-spacing-bottom">
                    Your shareholders have invested in {message.company.name} because they believe in your vision. Keep
                    them updated and show them the progress you&apos;re making.
                  </p>
                  <div style={{ marginTop: 'auto' }}>
                    <LinkAsButton
                      className="data-track-dashboard-cta-update-company-profile"
                      kind={'primary'}
                      url={updateUrl}
                    >
                      Update your shareholders
                    </LinkAsButton>
                  </div>
                </AgendaCardContainer>
              </Card>
            </div>
          );
        }

        const timeSinceUpdate = differenceInDays(startOfDay(new Date()), startOfDay(new Date(message.lastUpdated)));
        const color = timeSinceUpdate < 25 ? 'blue' : timeSinceUpdate < 35 ? 'orange' : 'red';
        return (
          <div key="message-update-investors" style={{ position: 'relative' }}>
            <CloseButtonWrapper>
              <IconButton size="small" onClick={updateInvestors.closeMessage} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </CloseButtonWrapper>
            <div className="u-flex" style={{ gap: '10px' }}>
              <Card
                onClick={() => push(updateUrl)}
                hover="highlight"
                color="blue"
                className="u-flex-grow"
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                <div className="u-flex u-flex--column u-flex-align-center">
                  <UpdateButton href={updateUrl}>
                    {/* there is some whitespace to the left that doesn't align the icon in the center */}
                    <PlusIcon style={{ marginLeft: '-1px' }} color="blue" fontSize="small" />
                  </UpdateButton>
                  <span className="u-half-spacing-top text-align-center">Update your shareholders</span>
                </div>
              </Card>
              <StatsCard
                padding="half"
                title="days since last update"
                className="u-flex-grow"
                value={timeSinceUpdate}
                color={color}
              />
            </div>
          </div>
        );
      case 'welcome-to-company-profile-as-new-company-admin':
        return <FinishCompanyProfile key="welcome-to-company-profile-as-new-company-admin" company={message.company} />;
      case 'should-update-company-profile-as-company-admin':
        if (updateCompanyProfile.isClosed) return null;
        return (
          <div key={`${message.company.id}-update-company-profile`} style={{ position: 'relative' }}>
            <CloseButtonWrapper>
              <IconButton size="small" onClick={updateCompanyProfile.closeMessage} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </CloseButtonWrapper>
            <Card>
              <AgendaCardContainer>
                <CardHeading heading="Make sure your company information is up to date!" />
                <p className="text-body u-content-spacing-bottom">
                  It has been {yearsMonthsDaysSince(message.lastUpdated)} since you last updated your company
                  information, perhaps some of it is outdated? Review your profile and make sure it&apos;s up to date.
                </p>
                <div style={{ marginTop: 'auto' }}>
                  <LinkAsButton
                    className="data-track-dashboard-cta-update-company-profile"
                    kind={'primary'}
                    url={companyUrls.overview(message.company.slug, 'profile')}
                  >
                    Go to company profile
                  </LinkAsButton>
                </div>
              </AgendaCardContainer>
            </Card>
          </div>
        );
      case 'pending-community-application-as-company-admin':
        if (pendingApplication.isClosed) return null;
        return (
          <div key="pending-community-application" style={{ position: 'relative' }}>
            <CloseButtonWrapper>
              <IconButton size="small" onClick={pendingApplication.closeMessage} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </CloseButtonWrapper>
            <Card>
              <AgendaCardContainer>
                <CardHeading heading="Application in progress" />
                <p className="text-body u-content-spacing-bottom">
                  Your application to {message.community.name} is currently being evaluated
                </p>
                <div style={{ marginTop: 'auto' }}>
                  <LinkAsButton
                    className="data-track-dashboard-cta-pending-application"
                    kind={'primary'}
                    url={communityUrls.overview(message.community.slug)}
                  >
                    Go to community profile
                  </LinkAsButton>
                </div>
              </AgendaCardContainer>
            </Card>
          </div>
        );
      default:
        return null;
    }
  });
}
